import React, { useEffect, useMemo, useRef, useState } from "react"

import { addDays, format as formatDate, isBefore, setHours, startOfDay, subDays } from "date-fns"
import { format } from "date-fns"
import {
  AlertTriangle,
  CalendarDays,
  ChevronDown,
  Clock,
  Download,
  MapPin,
  Maximize,
  Package,
  RotateCcw,
  TrendingUp,
  Truck,
  User,
} from "lucide-react"
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { toast } from "sonner"

import { EmptyState } from "@/components/shared/EmptyState"
import { LoadingState } from "@/components/shared/LoadingState"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { getSamsaraRouteUrl } from "@/config/constants"
import { useRouteInstancesForDate, useSyncStatus } from "@/hooks/useCollections"
import { formatDayCompact, formatDayLong, formatDayShort } from "@/lib/dateFormats"
import { depotsAgree, displayDepot } from "@/lib/shiftCover"
import { formatSyncAge, freshnessOf, freshnessTone } from "@/lib/syncFreshness"
import { toneClasses } from "@/lib/tone"
import { arrivalOf } from "@/lib/turnaroundArrival"
import { turnaroundsFrom } from "@/lib/turnarounds"
import { cn } from "@/lib/utils"
import { VOLUME_SOURCE_LABELS } from "@/lib/volumeReporting"
import { TurnaroundRoute } from "@/types/collections"
import { buildCsv, downloadCsv } from "@/lib/csvExport"
import { copyRichText } from "@/lib/clipboard"
import {
  ALL_DEPOTS,
  buildTurnaroundsPlan,
  buildTurnaroundsTable,
  isHgvTurnaround,
  renderPlanHtml,
  renderPlanText,
} from "@/lib/turnaroundsExport"

import TurnaroundArrivalCard from "./TurnaroundArrivalCard"
import { getDelayColor, getStatusColor, getStatusIcon } from "./turnaroundStatus"

/** The last tally counts whether the route turns around at all, hence a per-row predicate. */
const STATUS_TALLIES: ReadonlyArray<{
  label: string
  dot: string
  matches: (turnaround: TurnaroundRoute) => boolean
}> = [
  {
    label: "Not Started",
    dot: "bg-palette-jungleGreen400",
    matches: (t) => t.status === "not_started",
  },
  { label: "En Route", dot: "bg-blue-500", matches: (t) => t.status === "en_route" },
  { label: "Final Approach", dot: "bg-orange-500", matches: (t) => t.status === "final_approach" },
  { label: "Arrived", dot: "bg-amber-500", matches: (t) => t.status === "arrived" },
  { label: "Processing", dot: "bg-purple-500", matches: (t) => t.status === "processing" },
  { label: "Turnarounds", dot: "bg-emerald-500", matches: (t) => t.hasTurnaround },
]

/**
 * Ticks on its own so the board isn't re-rendered every second; aligned to the second boundary so
 * header and footer agree.
 */
const LiveClock: React.FC = () => {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined
    const timeout = setTimeout(() => {
      setNow(new Date())
      interval = setInterval(() => setNow(new Date()), 1000)
    }, 1000 - (Date.now() % 1000))
    return () => {
      clearTimeout(timeout)
      clearInterval(interval)
    }
  }, [])

  return <>{format(now, "HH:mm:ss")}</>
}

const TurnaroundsView: React.FC = () => {
  const [selectedDepot, setSelectedDepot] = useState<string>(ALL_DEPOTS)
  const [hgvOnly, setHgvOnly] = useState<boolean>(false)
  const [hideDepartedCompleted, setHideDepartedCompleted] = useState<boolean>(false)
  const [currentTime, setCurrentTime] = useState(new Date())

  // Data age, not time of day: reports whichever integration is furthest behind.
  const { data: syncStatus } = useSyncStatus()
  const oldestSyncAt = useMemo(() => {
    const stamps = [syncStatus?.samsara?.started_at, syncStatus?.trackpod?.started_at].filter(
      (stamp): stamp is string => !!stamp
    )
    if (stamps.length === 0) return null
    return stamps.reduce((oldest, stamp) => (stamp < oldest ? stamp : oldest))
  }, [syncStatus])
  const dataFreshness = freshnessOf(oldestSyncAt, currentTime)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [selectedDateStr, setSelectedDateStr] = useState<string | null>(null)
  const [vehicleChartMode, setVehicleChartMode] = useState<"at_depot" | "arrivals">("arrivals")

  const dateOptions = useMemo(() => {
    const today = startOfDay(new Date())
    return Array.from({ length: 15 }, (_, i) => {
      const date = addDays(today, i)
      return {
        dateStr: formatDate(date, "yyyy-MM-dd"),
        label: i === 0 ? "Today" : formatDayCompact(date),
        isToday: i === 0,
      }
    })
  }, [])

  // No date selected: use the 7am cutoff. Otherwise use the selected date.
  const dateToFetch = useMemo(() => {
    if (selectedDateStr) {
      return new Date(selectedDateStr + "T12:00:00")
    }
    const now = currentTime
    const sevenAMToday = setHours(now, 7)
    if (isBefore(now, sevenAMToday)) {
      return subDays(now, 1)
    }
    return now
  }, [currentTime, selectedDateStr])

  const dateStr = formatDate(dateToFetch, "yyyy-MM-dd")
  const isViewingToday = !selectedDateStr
  const { data: routeInstancesData, isLoading } = useRouteInstancesForDate(dateStr)

  // Recomputed each minute; only the "final approach" status reads the clock.
  const turnarounds = useMemo(
    () => turnaroundsFrom(routeInstancesData, currentTime),
    [routeInstancesData, currentTime]
  )

  // Named as the plan names them; the filter compares tolerantly.
  const availableDepots = [
    ALL_DEPOTS,
    "HIVED Hayes Depot",
    "HIVED Silvertown Depot",
    "HIVED Southampton Depot",
  ]

  // Minute resolution; only LiveClock ticks every second.
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60_000)
    return () => clearInterval(interval)
  }, [])

  const fullscreenRef = useRef<HTMLDivElement>(null)

  const toggleFullscreen = async () => {
    if (!fullscreenRef.current) return

    if (!document.fullscreenElement) {
      try {
        await fullscreenRef.current.requestFullscreen()
        setIsFullscreen(true)
      } catch (err) {
        console.error("Error attempting to enable fullscreen:", err)
      }
    } else {
      try {
        await document.exitFullscreen()
        setIsFullscreen(false)
      } catch (err) {
        console.error("Error attempting to exit fullscreen:", err)
      }
    }
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange)
  }, [])

  /** Applied first: the counts, chart, rows and both exports all read off it. */
  const scopedTurnarounds = useMemo(
    () => (hgvOnly ? turnarounds.filter(isHgvTurnaround) : turnarounds),
    [turnarounds, hgvOnly]
  )

  const filteredTurnarounds = scopedTurnarounds
    .filter((t) => {
      const matchesDepot = selectedDepot === ALL_DEPOTS || depotsAgree(t.depot, selectedDepot)

      if (hideDepartedCompleted && (t.status === "departed" || t.status === "completed")) {
        return false
      }

      return matchesDepot
    })
    .sort((a, b) => arrivalOf(a).at.getTime() - arrivalOf(b).at.getTime())

  /** Exports the filtered rows on screen, in screen order. */
  const handleExport = () => {
    if (filteredTurnarounds.length === 0) return

    const { headers, rows } = buildTurnaroundsTable(filteredTurnarounds, dateStr)
    downloadCsv(`turnarounds${hgvOnly ? "_hgv" : ""}_${dateStr}.csv`, buildCsv(headers, rows))
  }

  const hgvTurnarounds = filteredTurnarounds.filter(isHgvTurnaround)

  const handleCopyForSlack = async () => {
    if (hgvTurnarounds.length === 0) return

    const plan = buildTurnaroundsPlan(filteredTurnarounds, dateToFetch, selectedDepot)

    try {
      const flavour = await copyRichText(renderPlanText(plan), renderPlanHtml(plan))
      toast.success(
        `${hgvTurnarounds.length} HGV turnaround(s) copied`,
        flavour === "plain"
          ? { description: "Plain text — this browser blocked the styling" }
          : undefined
      )
    } catch {
      toast.error("Could not reach the clipboard — copy is blocked in this browser")
    }
  }

  const chartData = useMemo(() => {
    const depotFiltered = scopedTurnarounds.filter((t) => {
      return selectedDepot === ALL_DEPOTS || depotsAgree(t.depot, selectedDepot)
    })

    if (depotFiltered.length === 0) return []

    interface ChartSlot {
      time: string
      volume: number
      vehicles: number
      arrivals: number
      routes: { code: string; customers: string; volume: number; arrival: string }[]
    }

    const slots: ChartSlot[] = []
    const baseDate = startOfDay(dateToFetch)

    // 07:00 today through 06:30 next day: the operational day.
    const timeSlots: { hour: number; min: number; nextDay: boolean }[] = []
    for (let hour = 7; hour <= 23; hour++) {
      for (let min = 0; min < 60; min += 30) {
        timeSlots.push({ hour, min, nextDay: false })
      }
    }
    for (let hour = 0; hour <= 6; hour++) {
      for (let min = 0; min < 60; min += 30) {
        timeSlots.push({ hour, min, nextDay: true })
      }
    }

    for (const slot of timeSlots) {
      const timeStr = `${String(slot.hour).padStart(2, "0")}:${String(slot.min).padStart(2, "0")}`
      const slotStart = new Date(baseDate)
      if (slot.nextDay) slotStart.setDate(slotStart.getDate() + 1)
      slotStart.setHours(slot.hour, slot.min, 0, 0)
      const slotEnd = new Date(slotStart)
      slotEnd.setMinutes(slotEnd.getMinutes() + 30)

      let volume = 0
      let vehicleCount = 0
      let arrivalCount = 0
      const routesAtDepot: ChartSlot["routes"] = []

      for (const t of depotFiltered) {
        const arrivalTime = arrivalOf(t).at
        const departureTime = t.nextDeparture

        // `volumeReported` so the curve matches the figure in each row.
        if (arrivalTime >= slotStart && arrivalTime < slotEnd) {
          volume += t.volumeReported
          arrivalCount++
        }

        // Terminated routes assume 60 minutes of processing.
        const effectiveDeparture = departureTime || new Date(arrivalTime.getTime() + 60 * 60 * 1000)
        if (arrivalTime < slotEnd && effectiveDeparture > slotStart) {
          vehicleCount++
          routesAtDepot.push({
            code: t.routeCode,
            customers: t.customerNames?.join(", ") || "—",
            volume: t.volumeReported,
            arrival: format(arrivalTime, "HH:mm"),
          })
        }
      }

      slots.push({
        time: timeStr,
        volume,
        vehicles: vehicleCount,
        arrivals: arrivalCount,
        routes: routesAtDepot,
      })
    }

    return slots
  }, [scopedTurnarounds, selectedDepot, dateToFetch])

  return (
    <div
      ref={fullscreenRef}
      className={cn(
        "flex h-full flex-1 flex-col",
        "bg-palette-jungleGreen000"
      )}
    >
      <div className="border-b border-palette-jungleGreen200 bg-white px-4 py-3 lg:px-6 lg:py-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
          <div className="flex items-center justify-between gap-4 lg:justify-start">
            <div className="flex items-center gap-2">
              <div>
                <h2 className="text-base font-semibold text-palette-jungleGreen900 lg:text-lg">
                  Arrivals Board
                </h2>
                <p className="text-xs text-palette-jungleGreen500">
                  {isViewingToday
                    ? "Live turnaround status"
                    : `Planned — ${formatDayLong(dateToFetch)}`}
                </p>
              </div>
            </div>

            <div className="flex flex-shrink-0 items-center gap-2 rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen100 px-3 py-1.5 lg:ml-8 lg:px-4 lg:py-2">
              <Clock size={16} className="text-amber-500 lg:h-[18px] lg:w-[18px]" />
              <span className="font-mono text-lg font-bold tracking-wider text-palette-jungleGreen900 lg:text-2xl">
                <LiveClock />
              </span>
            </div>
          </div>

          {/* Grouped so controls wrap in two rows on a phone; `lg:contents` dissolves the groups
              on desktop. */}
          <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap lg:gap-3">
            <div className="flex items-center gap-2 lg:contents">
              <div className="flex items-center gap-2 rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000 px-3 py-2">
                <Checkbox
                  id="hgv-only"
                  checked={hgvOnly}
                  onCheckedChange={(checked) => setHgvOnly(checked === true)}
                  className="border-palette-jungleGreen300"
                />
                <Label
                  htmlFor="hgv-only"
                  className="flex cursor-pointer items-center gap-1.5 text-sm font-normal text-palette-jungleGreen700"
                >
                  <Truck size={14} className="text-palette-jungleGreen400" />
                  HGV only
                </Label>
              </div>

              <div className="flex items-center gap-2 rounded-lg border border-palette-jungleGreen200 bg-palette-jungleGreen000 px-3 py-2">
                <Checkbox
                  id="hide-departed-completed"
                  checked={hideDepartedCompleted}
                  onCheckedChange={(checked) => setHideDepartedCompleted(checked === true)}
                  className="border-palette-jungleGreen300"
                />
                {/* The long label made the controls wrap three deep on a phone. */}
                <Label
                  htmlFor="hide-departed-completed"
                  className="cursor-pointer text-sm font-normal text-palette-jungleGreen700"
                >
                  <span className="lg:hidden">Hide done</span>
                  <span className="hidden lg:inline">Hide Departed/Terminated</span>
                </Label>
              </div>
            </div>

            <div className="flex min-w-0 flex-1 items-center gap-2 lg:contents">
              <Select value={selectedDepot} onValueChange={setSelectedDepot}>
                <SelectTrigger className="min-w-0 flex-1 border-palette-jungleGreen200 bg-white text-palette-jungleGreen900 lg:w-48 lg:flex-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-palette-jungleGreen200 bg-white">
                  {availableDepots.map((depot) => (
                    <SelectItem
                      key={depot}
                      value={depot}
                      className="text-palette-jungleGreen900 hover:bg-palette-jungleGreen100"
                    >
                      {depot}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={filteredTurnarounds.length === 0}
                    title={
                      filteredTurnarounds.length === 0
                        ? "Nothing on the board to export"
                        : `Export ${filteredTurnarounds.length} row(s)`
                    }
                    className="hidden gap-2 border-palette-jungleGreen200 bg-white text-palette-jungleGreen600 hover:bg-palette-jungleGreen000 hover:text-palette-jungleGreen900 lg:inline-flex"
                  >
                    <Download size={16} />
                    Export
                    <ChevronDown size={14} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuItem onClick={handleExport} className="flex-col items-start gap-0.5">
                    <span className="text-xs font-medium">
                      Download CSV ({filteredTurnarounds.length})
                    </span>
                    <span className="text-[10px] text-palette-jungleGreen500">
                      One row per turnaround, carrying the plan
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleCopyForSlack}
                    disabled={hgvTurnarounds.length === 0}
                    className="flex-col items-start gap-0.5"
                  >
                    <span className="text-xs font-medium">
                      Copy for Slack ({hgvTurnarounds.length})
                    </span>
                    <span className="text-[10px] text-palette-jungleGreen500">
                      A pasteable HGV plan, grouped by depot
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="outline"
                size="icon"
                onClick={toggleFullscreen}
                className="hidden border-palette-jungleGreen200 bg-white text-palette-jungleGreen600 hover:bg-palette-jungleGreen000 hover:text-palette-jungleGreen900 lg:inline-flex"
              >
                <Maximize size={18} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-palette-jungleGreen200 bg-white px-4 py-2 lg:px-6 lg:py-2.5">
        <div className="flex items-center gap-3">
          <CalendarDays size={14} className="flex-shrink-0 text-palette-jungleGreen400" />
          <div className="flex items-center gap-1 overflow-x-auto">
            {dateOptions.map((option) => (
              <button
                key={option.dateStr}
                onClick={() => setSelectedDateStr(option.isToday ? null : option.dateStr)}
                className={cn(
                  "whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                  (option.isToday && isViewingToday) || selectedDateStr === option.dateStr
                    ? "border border-amber-200 bg-amber-100 text-amber-800"
                    : "border border-transparent text-palette-jungleGreen500 hover:bg-palette-jungleGreen100 hover:text-palette-jungleGreen700"
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="hidden border-b border-palette-jungleGreen200 bg-white px-6 py-3 lg:block">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          {STATUS_TALLIES.map((tally) => (
            <div key={tally.label} className="flex items-center gap-2">
              <div className={cn("h-3 w-3 flex-shrink-0 rounded-full", tally.dot)} />
              <span className="whitespace-nowrap text-sm text-palette-jungleGreen500">
                {tally.label}
              </span>
              <span className="text-lg font-bold tabular-nums text-palette-jungleGreen900">
                {scopedTurnarounds.filter(tally.matches).length}
              </span>
            </div>
          ))}
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 lg:p-6">
          {/* Below lg the table rows are replaced by cards, not compressed. `showDepot` is a
              property of the board's filter, not the route. */}
          <div className="space-y-3 lg:hidden">
            {filteredTurnarounds.map((turnaround) => (
              <TurnaroundArrivalCard
                key={turnaround.id}
                turnaround={turnaround}
                showDepot={selectedDepot === ALL_DEPOTS}
              />
            ))}
          </div>

          {/* `lg:grid` rather than `grid`, so the grid starts at lg. */}
          <div className="hidden grid-cols-12 gap-4 rounded-t-lg border border-b-0 border-palette-jungleGreen200 bg-palette-jungleGreen100 px-4 py-3 lg:grid">
            <div className="col-span-1 text-xs font-semibold uppercase tracking-wider text-palette-jungleGreen500">
              Route
            </div>
            <div className="col-span-2 text-xs font-semibold uppercase tracking-wider text-palette-jungleGreen500">
              Vehicle & Driver
            </div>
            <div className="col-span-1 text-xs font-semibold uppercase tracking-wider text-palette-jungleGreen500">
              Depot Injection
            </div>
            <div className="col-span-1 text-xs font-semibold uppercase tracking-wider text-palette-jungleGreen500">
              Planned
            </div>
            <div className="col-span-1 text-xs font-semibold uppercase tracking-wider text-palette-jungleGreen500">
              Arrival
            </div>
            <div className="col-span-1 text-xs font-semibold uppercase tracking-wider text-palette-jungleGreen500">
              Status
            </div>
            <div className="col-span-1 text-xs font-semibold uppercase tracking-wider text-palette-jungleGreen500">
              Volume
            </div>
            <div className="col-span-2 text-xs font-semibold uppercase tracking-wider text-palette-jungleGreen500">
              Customers
            </div>
            <div className="col-span-1 text-xs font-semibold uppercase tracking-wider text-palette-jungleGreen500">
              Next
            </div>
            <div className="col-span-1 text-xs font-semibold uppercase tracking-wider text-palette-jungleGreen500">
              Next Stop
            </div>
          </div>

          <div className="hidden divide-y divide-palette-jungleGreen100 overflow-hidden rounded-b-lg border border-t-0 border-palette-jungleGreen200 lg:block">
            {filteredTurnarounds.map((turnaround) => (
              <div
                key={turnaround.id}
                className="grid grid-cols-12 gap-4 bg-white px-4 py-4 transition-colors hover:bg-palette-jungleGreen000"
              >
                <div className="col-span-1 flex items-center">
                  {turnaround.samsaraRouteId ? (
                    <a
                      href={getSamsaraRouteUrl(turnaround.samsaraRouteId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open this route in Samsara"
                      className="font-mono text-xs font-medium text-amber-600 no-underline transition-colors hover:text-amber-800"
                    >
                      {turnaround.routeCode}
                    </a>
                  ) : (
                    <span className="font-mono text-xs font-medium text-amber-600">
                      {turnaround.routeCode}
                    </span>
                  )}
                </div>

                <div className="col-span-2">
                  <div className="flex items-center gap-2">
                    <Truck size={14} className="text-palette-jungleGreen400" />
                    <span
                      className={cn(
                        "font-semibold",
                        turnaround.vehicleReg === "Unknown"
                          ? "italic text-palette-jungleGreen400"
                          : "text-palette-jungleGreen900"
                      )}
                    >
                      {turnaround.vehicleReg}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <User size={12} className="text-palette-jungleGreen400" />
                    <span
                      className={cn(
                        "text-sm",
                        turnaround.driverName === "Unknown"
                          ? "italic text-palette-jungleGreen400"
                          : "text-palette-jungleGreen500"
                      )}
                    >
                      {turnaround.driverName}
                    </span>
                  </div>
                </div>

                <div className="col-span-1 flex items-center">
                  {turnaround.depot ? (
                    <span
                      className="text-sm text-palette-jungleGreen700"
                      title={turnaround.depot}
                    >
                      {displayDepot(turnaround.depot) ?? turnaround.depot}
                    </span>
                  ) : (
                    <span className="text-sm italic text-palette-jungleGreen400">Unknown</span>
                  )}
                </div>

                <div className="col-span-1 flex items-start">
                  <span className="font-medium text-palette-jungleGreen500">
                    {format(turnaround.plannedArrival, "HH:mm")}
                  </span>
                </div>

                <div className="col-span-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium text-palette-jungleGreen900">
                      {format(arrivalOf(turnaround).at, "HH:mm")}
                    </span>
                    {!turnaround.actualArrival && turnaround.estimatedArrival && (
                      <Badge variant="outline" className="border-blue-200 text-xs text-blue-600">
                        Live ETA
                      </Badge>
                    )}
                  </div>
                  {turnaround.arrivalDelayMinutes !== undefined &&
                    turnaround.arrivalDelayMinutes !== 0 && (
                      <div
                        className={cn(
                          "mt-1 flex items-center gap-1 text-xs",
                          getDelayColor(turnaround.arrivalDelayMinutes)
                        )}
                      >
                        {turnaround.arrivalDelayMinutes > 0 ? (
                          <AlertTriangle size={10} />
                        ) : (
                          <TrendingUp size={10} />
                        )}
                        <span>
                          {turnaround.arrivalDelayMinutes > 0 ? "+" : ""}
                          {turnaround.arrivalDelayMinutes} min
                        </span>
                      </div>
                    )}
                </div>

                <div className="col-span-1 flex items-center">
                  <Badge
                    variant="outline"
                    className={cn(
                      "flex items-center gap-1.5 capitalize",
                      getStatusColor(turnaround.status)
                    )}
                  >
                    {getStatusIcon(turnaround.status)}
                    <span className="text-xs">{turnaround.status.replace("_", " ")}</span>
                  </Badge>
                </div>

                <div className="col-span-1">
                  <div className="flex items-center gap-1 text-palette-jungleGreen900">
                    <Package size={12} className="text-palette-jungleGreen400" />
                    <span className="font-medium tabular-nums">
                      {turnaround.volumeReported.toLocaleString()}
                    </span>
                  </div>
                  <div
                    className={cn(
                      "text-[10px]",
                      turnaround.volumeSource === "actual"
                        ? toneClasses("success").text
                        : turnaround.volumeSource === "partial"
                          ? toneClasses("info").text
                          : "text-palette-jungleGreen400"
                    )}
                  >
                    {VOLUME_SOURCE_LABELS[turnaround.volumeSource]}
                    {turnaround.volumeSource === "partial" &&
                      ` ${turnaround.stopsConfirmed}/${turnaround.stopsCarryingVolume}`}
                  </div>
                  {turnaround.volumeSource !== "forecast" &&
                    turnaround.volumeReported !== turnaround.volumeEstimated && (
                      <div className="text-[10px] tabular-nums text-palette-jungleGreen400">
                        fcst {turnaround.volumeEstimated.toLocaleString()}
                      </div>
                    )}
                  {turnaround.loadingUnitsReported != null &&
                    turnaround.loadingUnitsReported > 0 && (
                      <div className="text-[10px] text-palette-jungleGreen400">
                        {turnaround.loadingUnitsReported.toLocaleString()}{" "}
                        {(turnaround.loadingUnitType || "units").toLowerCase()}
                      </div>
                    )}
                </div>

                <div className="col-span-2">
                  {turnaround.customerNames && turnaround.customerNames.length > 0 ? (
                    <div className="text-sm text-palette-jungleGreen700">
                      {turnaround.customerNames.join(", ")}
                    </div>
                  ) : (
                    <div className="text-sm italic text-palette-jungleGreen400">No customers</div>
                  )}
                </div>

                <div className="col-span-1 flex items-center">
                  {turnaround.hasTurnaround ? (
                    <Badge className="border border-cyan-200 bg-cyan-100 text-cyan-700 hover:bg-cyan-200">
                      <RotateCcw size={10} className="mr-1" />
                      T/A
                    </Badge>
                  ) : (
                    <Badge className="border border-palette-jungleGreen200 bg-palette-jungleGreen100 text-palette-jungleGreen600">
                      Terminates
                    </Badge>
                  )}
                </div>

                <div className="col-span-1">
                  {turnaround.hasTurnaround && turnaround.nextDeparture ? (
                    <div className="space-y-1">
                      <div>
                        <div className="text-xs text-palette-jungleGreen400">Departing</div>
                        <div className="text-sm font-medium text-palette-jungleGreen900">
                          {format(turnaround.nextDeparture, "HH:mm")}
                        </div>
                      </div>
                      {turnaround.nextDestination && (
                        <div className="flex items-center gap-1 text-xs text-amber-600">
                          <MapPin size={10} />
                          <span className="truncate">{turnaround.nextDestination}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs italic text-palette-jungleGreen400">No outbound</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {isLoading && <LoadingState label="Loading turnaround data..." className="py-12" />}

          {!isLoading && filteredTurnarounds.length === 0 && (
            <EmptyState icon={RotateCcw}>
              {turnarounds.length === 0
                ? `No routes found for ${isViewingToday ? "today" : formatDayShort(dateToFetch)}`
                : hgvOnly
                  ? "No HGV turnarounds match the current filters"
                  : "No turnarounds found for selected depot"}
            </EmptyState>
          )}

          {chartData.length > 0 && (
            <div className="mt-6 hidden rounded-lg border border-palette-jungleGreen200 bg-white p-5 lg:block">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-palette-jungleGreen600" />
                  <h3 className="text-sm font-semibold text-palette-jungleGreen900">
                    Depot Activity Timeline
                  </h3>
                  <span className="ml-1 text-xs text-palette-jungleGreen400">
                    30-minute intervals
                  </span>
                </div>
                <div className="flex items-center gap-1 rounded-lg bg-palette-jungleGreen100 p-0.5">
                  <button
                    onClick={() => setVehicleChartMode("arrivals")}
                    className={cn(
                      "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                      vehicleChartMode === "arrivals"
                        ? "bg-white text-palette-jungleGreen900 shadow-sm"
                        : "text-palette-jungleGreen500 hover:text-palette-jungleGreen700"
                    )}
                  >
                    Arrivals
                  </button>
                  <button
                    onClick={() => setVehicleChartMode("at_depot")}
                    className={cn(
                      "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                      vehicleChartMode === "at_depot"
                        ? "bg-white text-palette-jungleGreen900 shadow-sm"
                        : "text-palette-jungleGreen500 hover:text-palette-jungleGreen700"
                    )}
                  >
                    At Depot
                  </button>
                </div>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 11, fill: "#9ca3af" }}
                      tickLine={false}
                      axisLine={{ stroke: "#e5e7eb" }}
                      interval={1}
                    />
                    <YAxis
                      yAxisId="volume"
                      orientation="left"
                      tick={{ fontSize: 11, fill: "#9ca3af" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      yAxisId="vehicles"
                      orientation="right"
                      tick={{ fontSize: 11, fill: "#9ca3af" }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                        fontSize: "12px",
                        padding: "8px 12px",
                      }}
                      labelStyle={{ fontWeight: 600, marginBottom: "4px" }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: "12px", paddingTop: "12px" }}
                      iconType="circle"
                      iconSize={8}
                    />
                    <Bar
                      yAxisId="vehicles"
                      dataKey={vehicleChartMode === "at_depot" ? "vehicles" : "arrivals"}
                      fill="#60a5fa"
                      stroke="#3b82f6"
                      name={vehicleChartMode === "at_depot" ? "Vehicles at Depot" : "Arrivals"}
                      radius={[3, 3, 0, 0]}
                      barSize={14}
                    />
                    <Line
                      yAxisId="volume"
                      type="monotone"
                      dataKey="volume"
                      stroke="#f59e0b"
                      strokeWidth={2.5}
                      dot={{ fill: "#f59e0b", r: 3, strokeWidth: 0 }}
                      activeDot={{ r: 5, strokeWidth: 0 }}
                      name="Volume"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {isFullscreen && (
        <div className="border-t border-palette-jungleGreen200 bg-white px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Data age, not the wall clock: a ticking clock would look reassuring even when the
                sync has died. */}
            <div className="text-sm text-palette-jungleGreen500">
              {selectedDepot}
              {hgvOnly ? " • HGV only" : ""} • <LiveClock /> • Data{" "}
              <span className={cn(toneClasses(freshnessTone(dataFreshness)).text, "font-semibold")}>
                {formatSyncAge(oldestSyncAt, currentTime)}
              </span>
            </div>
            <div className="text-sm text-palette-jungleGreen500">Press ESC to exit fullscreen</div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TurnaroundsView
